// NPM
import React, {useEffect, useState } from 'react';
import classNames from 'classNames';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import _, { isEmpty, isNil, omit, omitBy, isUndefined } from 'lodash';
import { useHistory } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import moment from 'moment';
import { map } from 'lodash';

// ATOM
import {profileInfoAtom, passwordInfoAtom} from 'Recoil/profile.atom';

// SERVICE
import {authBusinessAccountChangePassword, authChangePassword} from 'Services/auth.services';

// UTIL
import errorHandler from 'Utils/error-handler';

// HOOK
import useSession from 'Hooks/session.hook';

// VALIDATOR
import { UpdateProfileFormSchema, AddressFormSchema } from './profile-form.validation';

// COMPONENT
import Address from './addresses';
import changePasswords from './change-passwords';

// ANT COMPONENT
import { UserOutlined, CalendarOutlined, PlusOutlined, EditOutlined, GiftTwoTone } from '@ant-design/icons';
import { AlertBox, Button, Avatar, TextInput, ImageUpload, DatePicker, PhoneInput, RadioGroup, BasicModal, SelectInput } from '@life-line-lab/components-react';
import { Avatar as AntAvatar, Space, Row, Col, Tabs, Form, Modal, Upload , Grid} from 'antd';

// HELPERS
import { toaster } from 'Helpers/toaster';

// STYLE
import './profile-form.scss';


//Functions
interface ProfileFormProps {
    data: any;
    // extraActions?: React.ReactNode;
    onSubmit: (input) => void;
}

interface AddressType {
    type: string;
    street1: string;
    street2: string;
    postcode: string;
    city: string;
    cityCode ?: string;
    state: string;
    stateCode ?: string;
    country: string;
}

interface passwordType {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

const ProfileForm: React.FC<ProfileFormProps> = ({ data, onSubmit }) => {
    
    const breakpoint = Grid.useBreakpoint();
    //Functions for Address Tab & Profile Tab 
    const initialAddress = {
        type: "",
        street1: "",
        street2: "",
        city: "",
        postcode: "",
        state: "",
        cityCode: "",
        stateCode: "",
        country: "MY",
    }

    const { TabPane } = Tabs;
    const initialInput = {
        fullName: '',
        dob: '',
        gender: '',
        primaryEmail: "",
        primaryAddress: {
            type: "",
            street1: "",
            street2: "",
            city: "",
            cityCode: "",
            stateCode: "",
            postcode: "",
            state: "",
            country: "",
        },
        addresses: [],
        primaryPhone: {
            dialCode: '',
            number: '',
            fullNumber: ''
        },

        identificationType: '',
        identificationNumber: '',
        logoBase64String: ""
    }

    const initialError = '';

    const handleSubmit = () => {
        onSubmit(input);
    }

    //Setting functions for Profile, Address & Change Password Tab
    const history = useHistory();
    const [ profileState, setProfileState ] = useRecoilState<any>(profileInfoAtom);
    const [ passwordState, setPasswordState ] = useRecoilState<any>(passwordInfoAtom);
    const { t } = useTranslation();
    const [input, setInput] = useState<any>(initialInput);
    const [error, setError] = useState<any>(initialError);
    const [activeTab, setActiveTab] = useState('1');
    const [addressModalVisible, setAddressModalVisible] = useState(false);
    const [updateStatus, setUpdateStatus] = useState(false);
    const [submitState, setSubmitState] = useState({ update: 'idle' });
    const [updateAddress, setUpdateAddress] = useState<any>(initialAddress);
    const [addressIndex, setAddressIndex] = useState<any>(-1);

    const changeTab = (key: string) => {
        setActiveTab(key);
    }

    useEffect(() => {
        console.log("data",data)
        if (!isEmpty(data)) {
            let tempAddresses:any = [];
            let tempData = data;
            if (!isEmpty(tempData?.addresses)) {
                tempAddresses = JSON.parse(JSON.stringify(tempData?.addresses));
                let firstAddress = tempAddresses[0];
                tempAddresses[0] = { ...tempData.primaryAddress, defaultAddress: true }
                tempAddresses.push(firstAddress);
            }
            else {
                tempAddresses[0] = { ...tempData.primaryAddress, defaultAddress: true };
            }
            setInput({ ...tempData, addresses: tempAddresses });
        }

    }, [data]);

    useEffect(() => {
        if (submitState.update == 'active') {
            updateProfile();
        }
        else if (submitState.update == 'failed') {
            toaster(
                'error',
                <div dangerouslySetInnerHTML={{
                    __html: (t('txt.profileUpdateFailed'))
                }} />
            )
        }
    }, [submitState.update])

    const handleDateChange = (value: moment.Moment, name: string) => {
        setError(initialError);
        setInput({ ...input, [name]: value });
    }

    const handleImageChange = (image: any, name: string) => {

        const isAllowedMimeType = image.type === 'image/jpeg' || image.type === 'image/png'

        if (!isAllowedMimeType) {
            setError({ ...error, general: t('error.invalidImageFormat') })
            setSubmitState({ ...submitState, update: 'failed' });
        }

        const isLt2M = image.size / 1024 / 1024 <= 1;

        if (!isLt2M) {
            setError({ ...error, general: t('error.exceedImageSizeLimit', { maxSize: '1MB ' }) })
            setSubmitState({ ...submitState, update: 'failed' });
        }

        if (isAllowedMimeType && isLt2M) {
            new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(image);
                reader.onload = () => resolve(reader.result);
            }).then((base64String: any) => {
                setError(initialError);
                setInput({ ...input, [name]: base64String });
                setSubmitState({ ...submitState, update: 'active' });
            });
        }

        return false;
    }

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setError(initialError);
        setInput({ ...input, [e.target.name]: e.target.value })
    } 

    const handleRadioChange = (value: string, name: string,) => {
        setError(initialError);
        setInput({ ...input, [name]: value });
    }

    const handleSelectChange = (value: string | string[], name: string,) => {
        setError(initialError);
        setInput({ ...input, [name]: value });
    }


    const editProfile = () => {
        setUpdateStatus(true);
    }

    const cancelEdit = () => {
        setUpdateStatus(false);
        setInput(data);
        setError(initialError);
    }

    const handleAddressChange = (value: string | string[], name: string) => {
        setUpdateAddress({ ...updateAddress, [name]: value });
    }

    const addEditAddress = () => {
        updateAddress.defaultAddress ? setUpdateAddress(_.omit(updateAddress, ['defaultAddress'])) : '';
        try {
            const value = updateProfile();
            if (!isEmpty(value)) {
                const value2 = AddressFormSchema.validateSync(omitBy({
                    ...updateAddress
                }, (value) => isNil(value) || value === ''), {
                    strict: true,
                    abortEarly: true,
                    stripUnknown: false
                });
                if (!isEmpty(value2)) {
                    let tempAddresses = JSON.parse(JSON.stringify(input));
                    if (tempAddresses.addresses) {
                        tempAddresses.addresses.map((address) => {
                            return address.defaultAddress ? _.omit(address, ['defaultAddress']) : address;
                        });
                    }
    
                    if (addressIndex > 0) {
                        tempAddresses.addresses[addressIndex] = value2;
                        setInput({ ...input, addresses: tempAddresses.addresses });
                    }
                    else if (addressIndex == 0) {
                        setInput({ ...input, primaryAddress: value2, addresses: tempAddresses.addresses });
                    }
                    else {
                        tempAddresses['addresses'].push(value2);
                        setInput({ ...input, addresses: tempAddresses.addresses });
                    }
                    closeModal();
                    setAddressIndex(-1);
                    setSubmitState({ ...submitState, update: 'active' });
                    setError([])
                }
                else {
                    setError({ ...error, general: t('error.validationFailed') });
                    setSubmitState({ ...submitState, update: 'idle' });
                    setUpdateStatus(true);
                }
            }
            else if (isEmpty(value)) {
                changeTab('1');
                closeModal();
                setAddressIndex(-1);
                toaster(
                    'error',
                    <div dangerouslySetInnerHTML={{
                        __html: t('txt.notComplete')
                    }} />
                )
            }
            else {
                setError({ ...error, general: t('error.validationFailed') });
                setSubmitState({ ...submitState, update: 'idle' });
                setUpdateStatus(true);
            }

        }
        catch (err) {
            let { path, value } = errorHandler.validation(err);
            if (document.querySelector(`input[name=${path}]`)) {

                (document.querySelector(`input[name=${path}]`) as HTMLInputElement).focus();
            }
            else if (document.querySelector(`div[name=${path}]`)) {

                (document.querySelector(`div[name=${path}] input`) as HTMLInputElement).focus();
            }
            setError({
                ...error,
                [path]: t(`${value}`, { field: t(`label.${path}`) })
            });
        }
    }

    const closeModal = () => {
        setUpdateAddress(initialAddress);
        setAddressModalVisible(false);
        setAddressIndex(-1);
        setError(initialError);
    }
    const updateProfile = () => {
        let tempInput = JSON.parse(JSON.stringify(input));
        if (input?.logoBase64String != '') {
            tempInput = _.omit(tempInput, ['file', 'image_file']);
        }
        else {
            tempInput = _.omit(tempInput, ['logoBase64String']);
        }

        if (tempInput?.addresses) {
            (tempInput?.addresses.length == 0 || !tempInput?.addresses) ? tempInput.addresses = [] : tempInput?.addresses.splice(0, 1);
        }
        try {
            const value = UpdateProfileFormSchema.validateSync(omitBy({
                ...tempInput,
                dob: tempInput.dob ? moment(tempInput.dob).toISOString() : undefined,
            }, (value) => isNil(value) || value === ''), {
                strict: true,
                abortEarly: true,
                stripUnknown: false
            });
            if (!isEmpty(value) && addressModalVisible==false && submitState.update == 'active') {
                onSubmit(value);
                setError([]);
                setUpdateStatus(false);
            }
            else {
                
                setError({ ...error, general: t('error.validationFailed') });
                setUpdateStatus(true);
            }
            setSubmitState({ ...submitState, update: 'idle' });
            return value;
        }
        catch (err) {
            // setUpdateStatus(true);
            let { path, value } = errorHandler.validation(err);
            if (path.indexOf('.') !== -1) {
                const str = path.split('.');
                path = str[0];
            }

            if (document.querySelector(`input[name=${path}]`)) {
                (document.querySelector(`input[name=${path}]`) as HTMLInputElement).focus();
            }
            else if (document.querySelector(`div[name=${path}]`)) {
                (document.querySelector(`div[name=${path}] input`) as HTMLInputElement).focus();
            }
            setError({
                ...error,
                [path]: t(`${value}`, { field: t(`label.${path}`) })
            });
            setSubmitState({ ...submitState, update: 'idle' });
        }
    }

    const deconstruct = (phone: { type, number, countryCode }) => {
        if (phone?.type) {
            const { type, number, countryCode } = phone;
            const dialCode = countryCode;
            return { dialCode, number }
        }
        return { dialCode: '', number: '' }
    }

    const handlePhoneChange = (value: { dialCode: string, number: string }, name: string) => {
        setError(initialError);
        value = !isNil(value) ? value : { dialCode: '60', number: '' };
        setInput({ ...input, [name]: value });
    }

    const handleSaveProfile = () => {
        setSubmitState({ ...submitState, update: 'active' });
    }


    const handleDeleteAddress = (index: number) => {
        try {
            const value = updateProfile();

            if (!isEmpty(value)) {
                let tempAddresses = JSON.parse(JSON.stringify(input));
                tempAddresses.addresses[0] = _.omit(tempAddresses.addresses[0], ['defaultAddress']);
                if (index !== 0 && tempAddresses.addresses.length > 1) {
                    tempAddresses.addresses.splice(index, 1);
                    // tempAddresses.addresses.splice(0, 1);
                }
                setAddressIndex(-1);
                setInput(tempAddresses);
                setSubmitState({ ...submitState, update: 'active' });
            }
            else if (isEmpty(value)) {
                changeTab('1');
                closeModal();
                setAddressIndex(-1);
                toaster(
                    'error',
                    <div dangerouslySetInnerHTML={{
                        __html: t('txt.notComplete')
                    }} />
                )
            }
            else {
                setError({ ...error, general: t('error.validationFailed') });
                setUpdateStatus(true);
            }
        }
        catch(err) {

        }
        
    }
    const handleEditAddress = (action: string, index: number) => {
        switch (action) {
            case 'DEFAULT':
                let tempAddresses = JSON.parse(JSON.stringify(input));

                //remove defaultAddress status from the first address
                tempAddresses.addresses[0] = _.omit(tempAddresses.addresses[0], ['defaultAddress']);
                //previous defaultAddress
                let previousPrimaryAddress = tempAddresses.primaryAddress;
                //assign new default address as primary address
                tempAddresses.primaryAddress = tempAddresses.addresses[index];
                tempAddresses.addresses[index] = previousPrimaryAddress;
                setAddressIndex(-1);
                setInput({ ...input, ...tempAddresses });
                setSubmitState({ ...submitState, update: 'active' });
                break;
            case 'UPDATE':
                setAddressModalVisible(true);
                setUpdateAddress(input.addresses[index]);
                setAddressIndex(index);
                break;
            case 'DELETE':
                handleDeleteAddress(index);
                break;
        }

    }
    //Functions for change Password Tab (Authentication & Toaster Responses)
    const handleOk = async (currentPassword:string, newPassword:string) => {
            
        try {
            setPasswordState({
                ...passwordState,
                status: {
                    ...passwordState.status, fetch: 'loading'
                }
            });
            const session = useSession();
            if (isEmpty(session)) {
                history.push('/login');
                return;
            }

            if (!session.profile?.isBusiness) {
                const userPID = session.roles[0].data.pid;
                const response = await authChangePassword(userPID,currentPassword,newPassword);
                
                if (response) {
                    setPasswordState({
                        ...passwordState,
                        status: {
                            ...passwordState.status, fetch: 'success'
                        },
                        data: response
                    }
                    ) //toaster for success
                    toaster(
                      'success',
                      <div dangerouslySetInnerHTML={{
                          __html: t('txt.resetPasswordSuccess')
                      }} />
                    );
                }else {
                    toaster(
                        'error',
                        <div dangerouslySetInnerHTML={{
                        __html: ('Current password is wrong, please try again.')
                        }} />
                    )
    
                    setProfileState({
                        ...passwordState,
                        status: {
                            ...passwordState.status, fetch: 'error'
                        }
                    });
                }
            } else {
                const response = await authBusinessAccountChangePassword(currentPassword,newPassword);
                
                if (response) {
                    setPasswordState({
                        ...passwordState,
                        status: {
                            ...passwordState.status, fetch: 'success'
                        },
                        data: response
                    }
                    ) //toaster for success
                    toaster(
                      'success',
                      <div dangerouslySetInnerHTML={{
                          __html: t('txt.resetPasswordSuccess')
                      }} />
                    );
                } else {
                    toaster(
                        'error',
                        <div dangerouslySetInnerHTML={{
                        __html: ('Current password is wrong, please try again.')
                        }} />
                    )
    
                    setProfileState({
                        ...passwordState,
                        status: {
                            ...passwordState.status, fetch: 'error'
                        }
                    });
                }
            }
        
      }
    catch (err) {
        toaster(
            'error',
            <div dangerouslySetInnerHTML={{
            __html: ('An error occurred while retrieving your information. Please refresh the page or contact us for help')
            }} />
        )
    }
    }
    return (
        <div style={{ height: 'inherit', width: '90%' }}>
            <form className="form">
                <Space direction="horizontal" size="middle">
                    <Avatar style={{ backgroundColor: '#196AAF' }} icon={<UserOutlined />} size="large" /> <h1 className="page-title">{t('pageTitle.account')}</h1>
                </Space>
                <Space className="form--inner" direction="vertical" size="large" align="start">

                    <Tabs className="tab-bar" onTabClick={changeTab} activeKey={activeTab} defaultActiveKey="1" animated={true} tabPosition={breakpoint.xs ? "top" : "left"}>
                        <TabPane tab={t('label.profileInformation')} key='1'>
                            <Row style={{ width: '100%',  alignItems: 'center'  }}>
                                <Col xxl={7} xl={7} lg={7} md={10} sm={10} xs={10}>
                                    <h3 className="title">{t('pageTitle.profile')}</h3>

                                </Col>
                                <Col style={{ display: 'flex', justifyContent:'flex-start' }} xxl={15} xl={15} lg={15} md={10} sm={10} xs={10}>
                                    <EditOutlined className="editIcon svg" 
                                                onClick={editProfile}
                                            />
                                    {/* <Button
                                        className='button--right'
                                        type="primary"
                                        size="middle"
                                        icon={<EditOutlined />}
                                        onClick={editProfile}
                                    />  */}
                                    {/* the change password button overlaps here in extraActions */}
                                    {/* {extraActions} */}

                                </Col>
                            </Row>
                            <Space className="form--inner--inner" direction="vertical" size="large" align="center">

                                <Row style={{rowGap: '20px'}}>
                                    <Col xxl={14} xl={14} lg={14} md={20} sm={20} xs={20} style={{ paddingRight: '20px', paddingLeft: '20px' }}>
                                        <Row className="text">
                                            <Col xxl={8} xl={8} lg={8} md={16} sm={16} xs={16}>
                                                <h3 className="text--left">{t('label.fullName')}</h3>
                                            </Col>
                                            <Col xxl={16} xl={16} lg={16} md={22} sm={22} xs={22}>
                                                {/* {updateStatus == true ? <TextInput
                                                    className="text--right"
                                                    name="fullName"
                                                    isError={!!error.fullName}
                                                    value={input.fullName}
                                                    onChange={(value) => handleTextChange(value)}
                                                /> :  */}
                                                <h4 className="text--right">{input.fullName}</h4>
                                                {/* } */}
                                                <AlertBox text={error.fullName} />
                                            </Col>
                                        </Row>
                                        <Row className="text">
                                            <Col xxl={8} xl={8} lg={8} md={16} sm={16} xs={16}>
                                                <h3 className="text--left">{t('label.gender')}</h3>
                                            </Col>
                                            <Col xxl={16} xl={16} lg={16} md={22} sm={22} xs={22}>
                                                {
                                                    updateStatus === true ?
                                                    <RadioGroup
                                                        className="text--right"
                                                        direction="horizontal"
                                                        isRequired={true}
                                                        disabled={updateStatus === true ?
                                                            false
                                                            : true}
                                                        bordered={false}
                                                        isError={!!error.gender}
                                                        name="gender"
                                                        value={input.gender}
                                                        onChange={(event) => handleRadioChange(event.target.value, 'gender')}
                                                        options=
                                                        {[
                                                            { value: 'MALE', label: t('option.male') },
                                                            { value: 'FEMALE', label: t('option.female') },
                                                        ]}
                                                    /> : 
                                                    <h4 className="text--right">{input?.gender ? t(`option.${input?.gender?.toLowerCase()}`) : '-'}</h4>
                                                }

                                                <AlertBox text={error.gender} />
                                            </Col>
                                        </Row>
                                        <Row className="text">
                                            <Col xxl={8} xl={8} lg={8} md={16} sm={16} xs={16}>
                                                <h3 className="text--left">{t('label.dob')}</h3>
                                            </Col>
                                            <Col xxl={16} xl={16} lg={16} md={22} sm={22} xs={22}>
                                                {updateStatus === true ?
                                                    <DatePicker
                                                        className="text--right"
                                                        isRequired={true}
                                                        isError={!!error.dob}
                                                        suffix={<CalendarOutlined />}
                                                        placeholder="YYYY-MM-DD"
                                                        value={input.dob ? moment(input.dob) : null}
                                                        disabledDate={
                                                            (currentDate: any) => {
                                                                return currentDate && currentDate.valueOf() > Date.now();
                                                            }
                                                        }
                                                        onChange={
                                                            (value: any) =>
                                                                handleDateChange(value, 'dob')
                                                        } /> :
                                                    <h4 className="text--right">{input.dob ? moment(input.dob).format("Do MMM YYYY").toString() : ''}</h4>
                                                }


                                                <AlertBox text={error.dob} />
                                            </Col>
                                        </Row>
                                        <Row className="text">
                                            <Col xxl={8} xl={8} lg={8} md={16} sm={16} xs={16}>
                                                <h3 className="text--left">{t('label.email')}</h3>
                                            </Col>
                                            <Col xxl={16} xl={16} lg={16} md={22} sm={22} xs={22}>
                                                {updateStatus == true ?
                                                    <TextInput
                                                        className="text--right"
                                                        name="primaryEmail"
                                                        isError={!!error.primaryEmail}
                                                        value={input.primaryEmail}
                                                        onChange={(value) => handleTextChange(value)}
                                                    /> :
                                                    <h4 className="text--right">{input.primaryEmail}</h4>}
                                                <AlertBox text={error.primaryEmail} />
                                            </Col>
                                        </Row>
                                        <Row className="text">
                                            <Col xxl={8} xl={8} lg={8} md={16} sm={16} xs={16}>
                                                <h3 className="text--left">{updateStatus == true ? t('label.identificationType') : t(`option.${input.identificationType?.toLowerCase()}`)}</h3>
                                            </Col>
                                            <Col xxl={16} xl={16} lg={16} md={22} sm={22} xs={22}>
                                                {/* <h3 className="text--right">{input.identification?.no}</h3> */}
                                                {updateStatus == true ?
                                                    <><SelectInput
                                                        isRequired={true}
                                                        isError={!!error.identificationType}
                                                        name="identificationType"
                                                        value={input.identificationType}
                                                        onChange={(value) => handleSelectChange(value, 'identificationType')}
                                                        options=
                                                        {[
                                                            { value: 'PASSPORT', label: t('option.passport') },
                                                            { value: 'NRIC', label: t('option.nric') },
                                                            { value: 'UN', label: t('option.un') },
                                                        ]}
                                                    />
                                                        <AlertBox text={error.identificationType} />
                                                    </> : ''}
                                                {updateStatus === true ? <TextInput
                                                    className="text--right"
                                                    name="identificationNumber"
                                                    isError={!!error.identificationNumber}
                                                    value={input.identificationNumber}
                                                    onChange={(value) => handleTextChange(value)}
                                                /> : <h4 className="text--right">{input.identificationNumber}</h4>}
                                                <AlertBox text={error.identificationNumber} />
                                            </Col>
                                        </Row>
                                        <Row className="text">
                                            <Col xxl={8} xl={8} lg={8} md={16} sm={16} xs={16}>
                                                <h3 className="text--left">{t('label.primaryPhone')}</h3>
                                            </Col>
                                            <Col xxl={16} xl={16} lg={16} md={23} sm={23} xs={23}>
                                                {updateStatus === true ?
                                                    <PhoneInput
                                                        isRequired={true}
                                                        isError={!!error.primaryPhone}
                                                        name={"primaryPhone"}
                                                        size="middle"
                                                        value={input.primaryPhone}
                                                        onChange={(value) => handlePhoneChange(value, 'primaryPhone')}
                                                    /> :
                                                    <h4 className="text--right">{`+${input.primaryPhone.dialCode}${input.primaryPhone.number}`}</h4>
                                                    }
                                                <AlertBox text={error.primaryPhone} />
                                            </Col>
                                        </Row>
                                        

                                    </Col>
                                    <Col 
                                    style={breakpoint.xs ? {
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignSelf: 'center',
                                        paddingTop:'30px',
                                        borderTopStyle: 'solid',
                                        borderColor:'#b9aeae',
                                        height: 'inherit'
                                    } : {display:'flex',
                                        justifyContent: 'center',
                                        alignSelf: 'center',
                                        borderLeftStyle: 'solid',
                                        borderColor:'#b9aeaeae',
                                        height: 'inherit'}} 
                                        xxl={10} xl={10} lg={10} md={22} sm={22} xs={22}>

                                        <div>
                                            <>
                                                <ImageUpload
                                                    className="image-frame"
                                                    imageUrl={input.logoBase64String ? input.logoBase64String : input?.image_file?.file_url}
                                                    buttonLabel={t('action.uploadImage')}
                                                    description={t('txt.imageUpload', { maxSize: '1MB' })}
                                                    name="logoBase64String"
                                                    isRequired={false}
                                                    isError={!!error.logoBase64String}
                                                    accept="image/png, image/jpeg"
                                                    maxCount={1}
                                                    beforeUpload={
                                                        (file: any) =>
                                                            handleImageChange(file, 'logoBase64String')
                                                    }
                                                />
                                                <AlertBox text={error.logoBase64String} />
                                            </>

                                            {(input?.logoBase64String || input?.image_file) &&
                                                <Upload
                                                    maxCount={1}
                                                    showUploadList={false}
                                                    accept="image/png, image/jpeg"
                                                    openFileDialogOnClick={true}
                                                    beforeUpload={
                                                        (file: any) =>
                                                            handleImageChange(file, 'logoBase64String')
                                                    }
                                                >
                                                    <Button className='upload-button' label={t('action.uploadImage')} />
                                                </Upload>}
                                        </div>

                                    </Col>
                                    <Row style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', width: '93%' }}>
                                    {updateStatus === true ? <><Button
                                                className='button--left'
                                                type="primary"
                                                size="middle"
                                                label={t('action.save')}
                                                onClick={handleSaveProfile}
                                            />
                                            
                                                <Button
                                                    className="button--right cancel"
                                                    type="primary"
                                                    size="middle"
                                                    label={t('action.cancel')}
                                                    onClick={cancelEdit}
                                                /> </>: ''}
                                    </Row>
                                </Row>
                                
                            </Space>

                        </TabPane>
                        <TabPane tab={t('label.addresses')} key='2'>

                            <Row style={{ width: '100%', alignItems: 'center' }}>
                                <Col xxl={19} xl={19} lg={19} md={10} sm={10} xs={10}>
                                    <h3 className="title">{t('pageTitle.addresses')}</h3>
                                </Col>

                                <Col style={{ display: 'flex' }} xxl={4} xl={4} lg={4} md={10} sm={10} xs={10}>
                                    <Button
                                        className='button--right'
                                        type="primary"
                                        size="middle"
                                        icon={<PlusOutlined />}
                                        label={t('action.addAddress')}
                                        onClick={() => setAddressModalVisible(true)}
                                    />
                                </Col>
                            </Row>
                            <Space className="form--inner--inner" direction="vertical" size="large" align="center">

                                <Address.AddressList list={input.addresses} onEdit={handleEditAddress} onDelete={handleDeleteAddress} />
                                <Modal
                                    destroyOnClose={true}
                                    okText={t('action.ok')}
                                    cancelText={t('action.cancel')}
                                    cancelButtonProps={{shape:"round", size:"middle"}}
                                    okButtonProps={{shape:"round", size:"middle"}}
                                    bodyStyle={{ height: 'inherit' }} width={700}
                                    visible={addressModalVisible}
                                    onOk={addEditAddress}
                                    onCancel={closeModal}
                                >
                                    <Address.AddressForm index={addressIndex} isError={error} data={updateAddress} onChange={handleAddressChange} />
                                </Modal>

                            </Space>
                            </TabPane>
                            <TabPane tab={t('action.changePassword')} key='3'>
                                <Row style={{ width: '100%', padding: '20px 0px 0px 20px', alignItems: 'center' }}>
                                    <Col xxl={19} xl={19} lg={19} md={10} sm={10} xs={10}>
                                        <h3 className="title">{t('pageTitle.changePassword')}</h3>
                                        </Col>
                                </Row>
                                <changePasswords.PasswordForm
                                    onSubmit={handleOk} 
                                    />
                            </TabPane>
                            {/* <TabPane tab={<span> <GiftTwoTone twoToneColor="#442f84" style={{fontSize: '20px'}}/> My Referrals </span>} key='4'>
                                <Row style={{ width: '100%',  alignItems: 'center' }}>
                                    <Col xxl={19} xl={19} lg={19} md={10} sm={10} xs={10}>
                                        <h3 className="title">My Referrals Dummy Page</h3>
                                        </Col>
                                </Row>
                            </TabPane> */}
                    </Tabs>
                </Space>
            </form>
        </div>

    )
}

export default ProfileForm;
